"use client";

import Image from "next/image";
import { useMemo, useState } from "react";
import { FieldPlaceholder } from "@/components/ui/Section";

/** What one card needs, resolved on the server: `state` is already the name. */
export interface PublicCommentCard {
  slug: string;
  title: string;
  href: string;
  date: string;
  agency?: string;
  rule?: string;
  topic?: string;
  state?: string;
  image?: { src: string };
}

const ALL = "";

/**
 * The /public-comments listing and its two filters.
 *
 * Filtered in the browser rather than by query string: the whole list is one
 * small ISR page, and a searchParams read would make the route dynamic, which
 * puts a cold-startable Lambda in front of every crawler for no gain.
 *
 * The filter options are whatever the published comments carry, so a new state
 * or topic shows up the day its first comment is published and an option can
 * never lead to an empty list.
 */
export function PublicCommentList({ comments }: { comments: PublicCommentCard[] }) {
  const [state, setState] = useState(ALL);
  const [topic, setTopic] = useState(ALL);

  const states = useMemo(() => distinct(comments.map((c) => c.state)), [comments]);
  const topics = useMemo(() => distinct(comments.map((c) => c.topic)), [comments]);
  const shown = comments.filter(
    (c) => (state === ALL || c.state === state) && (topic === ALL || c.topic === topic),
  );

  if (comments.length === 0) {
    return (
      <p className="border-t-2 border-ink pt-10 text-[16px] text-ink-60">
        No public comments have been posted yet.
      </p>
    );
  }

  return (
    <div className="border-t-2 border-ink pt-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="flex flex-wrap gap-5">
          <Filter label="State" value={state} onChange={setState} all="All states" options={states} />
          <Filter label="Topic" value={topic} onChange={setTopic} all="All topics" options={topics} />
        </div>
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-60" aria-live="polite">
          {shown.length} {shown.length === 1 ? "comment" : "comments"} · Newest first
        </p>
      </div>

      {shown.length === 0 ? (
        <p className="mt-12 text-[16px] text-ink-60">
          No comments match those filters.{" "}
          <button
            type="button"
            className="text-brass-deep underline underline-offset-4"
            onClick={() => {
              setState(ALL);
              setTopic(ALL);
            }}
          >
            Show all comments
          </button>
        </p>
      ) : (
        <ul className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((c) => (
            <li key={c.slug}>
              <Card comment={c} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Card({ comment: c }: { comment: PublicCommentCard }) {
  const source = [c.agency, c.rule].filter(Boolean).join(" · ");
  return (
    <a
      href={c.href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex h-full flex-col overflow-hidden rounded-sm border border-line bg-white transition-shadow hover:shadow-md"
    >
      <div className="relative border-b border-line">
        {c.image ? (
          // The top of the PDF's first page: its letterhead, date and RE line,
          // which is what identifies a filing at a glance.
          <div className="relative aspect-[16/10] w-full overflow-hidden bg-white">
            <Image
              src={c.image.src}
              alt=""
              fill
              sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
              className="object-cover object-top"
            />
          </div>
        ) : (
          <FieldPlaceholder label="Public comment" ratio="16/10" tone="paper2" className="rounded-none" />
        )}
        <span className="absolute right-3 top-3 rounded-[2px] bg-ink px-2 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em] text-paper">
          PDF
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h2 className="font-display text-[21px] font-semibold leading-snug transition-colors group-hover:text-brass-deep">
          {c.title}
        </h2>
        {source && <p className="mt-3 font-display text-[15px] leading-relaxed text-ink-60">{source}</p>}
        <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 border-t border-line pt-4 font-display text-[15px]">
          {c.state && <Fact term="State" value={c.state} />}
          {c.topic && <Fact term="Topic" value={c.topic} />}
          <Fact term="Filed" value={formatDate(c.date)} />
        </dl>
        <span className="mt-auto pt-7 font-display text-[16px] font-semibold text-brass-deep underline underline-offset-4">
          Read the comment <span aria-hidden>→</span>
        </span>
      </div>
    </a>
  );
}

function Fact({ term, value }: { term: string; value: string }) {
  return (
    <>
      <dt className="font-semibold text-brass-deep">{term}</dt>
      <dd className="text-ink">{value}</dd>
    </>
  );
}

function Filter({
  label,
  value,
  onChange,
  all,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  all: string;
  options: string[];
}) {
  return (
    <label className="grid gap-2">
      <span className="font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-brass-deep">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 min-w-[200px] rounded-sm border border-line-strong bg-white px-3 text-[15px] text-ink"
      >
        <option value={ALL}>{all}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function distinct(values: (string | undefined)[]): string[] {
  return [...new Set(values.filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b));
}

function formatDate(iso: string) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
